/**
 * New workflow: start blank, or from one of the server's templates. A
 * template that needs a setting it cannot know (which rotation, which
 * person) asks for it here, before the workflow is made.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Button, Card, Group, Modal, Select, SimpleGrid, Stack, Text, TextInput, ThemeIcon, UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconFile, IconTemplate } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useAssignmentGroups } from '../../api/assignment';
import { useEmailTemplates } from '../../api/queries';
import { useWorkflowEdits, type WorkflowTemplate } from '../../api/workflows';
import { TOKENS } from '../../theme';
import { useVocab } from '../../vocab';
import { useOwnerOptions } from '../crm/crmShared';
import { cfg, fillNeeds, parseNeeds, stepLabel, waitSentence } from './workflowModel';

function NeedInput({
  stepLabelText, setting, value, onChange,
}: { stepLabelText: string; setting: string; value: string; onChange: (x: string) => void }) {
  const groups = useAssignmentGroups(setting === 'group_id').data?.groups ?? [];
  const owners = useOwnerOptions(setting === 'user_id');
  const templates = useEmailTemplates().data?.templates ?? [];
  if (setting === 'template_id') {
    return (
      <Select
        label={`${stepLabelText}: which email`}
        description="Made under Emails, Templates. The brand is the default one; change it on the step."
        placeholder="Choose an email template"
        searchable
        data={templates.map((t) => ({ value: t.id, label: t.name }))}
        value={value || null}
        onChange={(x) => onChange(x ?? '')}
        nothingFoundMessage="No email templates yet. Make one under Emails, Templates."
        data-testid="need-template_id"
      />
    );
  }
  if (setting === 'group_id') {
    return (
      <Select
        label={`${stepLabelText}: which rotation`}
        description="The next rep in it gets each one, its caps and hours respected."
        placeholder="Choose a rotation"
        data={groups.map((g) => ({ value: g.id, label: g.name }))}
        value={value || null}
        onChange={(x) => onChange(x ?? '')}
        nothingFoundMessage="No rotations yet. Make one in Settings, Teams."
        data-testid="need-group_id"
      />
    );
  }
  if (setting === 'user_id') {
    return (
      <Select
        label={`${stepLabelText}: who`}
        placeholder="Choose who"
        searchable
        data={owners}
        value={value || null}
        onChange={(x) => onChange(x ?? '')}
        data-testid="need-user_id"
      />
    );
  }
  return (
    <TextInput
      label={`${stepLabelText}: ${setting.replace(/_/g, ' ')}`}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      data-testid={`need-${setting}`}
    />
  );
}

export function TemplatePicker({
  opened, onClose, templates, starter,
}: {
  opened: boolean;
  onClose: () => void;
  templates: WorkflowTemplate[];
  /** Open at this starter's key, as the Emails page's "Email a list" does. */
  starter?: string | null;
}) {
  const v = useVocab();
  const navigate = useNavigate();
  const { create } = useWorkflowEdits();
  const [chosen, setChosen] = useState<WorkflowTemplate | null>(null);
  const [filled, setFilled] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  useEffect(() => {
    if (!opened) {
      setChosen(null);
      setFilled({});
      setError('');
    }
  }, [opened]);
  useEffect(() => {
    const t = opened && starter ? templates.find((x) => x.key === starter) : undefined;
    if (t) {
      setChosen(t);
      setFilled({});
    }
  }, [opened, starter, templates]);

  async function make(t: WorkflowTemplate, values: Record<string, string>) {
    setError('');
    try {
      const w = await create.mutateAsync({
        name: t.name,
        description: t.description,
        trigger: t.trigger,
        conditions: t.conditions,
        steps: fillNeeds(t.steps, values),
        enabled: false,
      });
      notifications.show({ color: 'teal', title: `${v.One('workflow')} made`, message: `${w.name}. It is off until you switch it on.` });
      onClose();
      navigate(`/automations/${encodeURIComponent(w.id)}`);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  function pick(t: WorkflowTemplate) {
    if (t.needs.length === 0) {
      void make(t, {});
      return;
    }
    setChosen(t);
    setFilled({});
  }

  const needs = chosen ? parseNeeds(chosen.needs) : [];
  const missing = needs.some((n) => !filled[`${n.stepId}.${n.key}`]);

  return (
    <Modal opened={opened} onClose={onClose} title={`New ${v.one('workflow')}`} size="xl">
      {chosen ? (
        <Stack gap="md" data-testid="template-needs">
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconArrowLeft size={14} />}
            onClick={() => setChosen(null)}
            style={{ alignSelf: 'flex-start' }}
          >
            Back
          </Button>
          <div>
            <Text fw={600}>{chosen.name}</Text>
            <Text size="sm" c="dimmed">{chosen.description}</Text>
          </div>
          <Text size="sm">Choose this before it is made:</Text>
          {needs.map((n) => {
            const step = chosen.steps.find((s) => s.id === n.stepId);
            const key = `${n.stepId}.${n.key}`;
            return (
              <NeedInput
                key={key}
                stepLabelText={step ? stepLabel(v, step.kind) : n.stepId}
                setting={n.key}
                value={filled[key] ?? ''}
                onChange={(x) => setFilled((f) => ({ ...f, [key]: x }))}
              />
            );
          })}
          {error && <Alert color="red" variant="light">{error}</Alert>}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>Cancel</Button>
            <Button onClick={() => make(chosen, filled)} disabled={missing} loading={create.isPending}>
              {`Make the ${v.one('workflow')}`}
            </Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap="md" data-testid="template-picker">
          <Text size="sm" c="dimmed">
            {`Start from a template or from nothing. A new ${v.one('workflow')} is off until you switch it on, so you can test it first.`}
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TemplateTile
              title={`A blank ${v.one('workflow')}`}
              description="Choose the trigger, the conditions and every step yourself."
              steps={[]}
              blank
              onClick={() => { onClose(); navigate('/automations/new'); }}
            />
            {templates.map((t) => (
              <TemplateTile
                key={t.key}
                title={t.name}
                description={t.description}
                steps={t.steps.map((s) => (s.kind === 'wait' ? waitSentence(cfg(s)) : stepLabel(v, s.kind)))}
                onClick={() => pick(t)}
                testId={`template-${t.key}`}
              />
            ))}
          </SimpleGrid>
          {error && <Alert color="red" variant="light">{error}</Alert>}
        </Stack>
      )}
    </Modal>
  );
}

function TemplateTile({
  title, description, steps, blank, onClick, testId,
}: {
  title: string;
  description: string;
  steps: string[];
  blank?: boolean;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <UnstyledButton onClick={onClick} data-testid={testId ?? 'template-blank'} style={{ height: '100%' }}>
      <Card padding="sm" withBorder style={{ height: '100%' }}>
        <Stack gap={6}>
          <Group gap="xs" wrap="nowrap">
            <ThemeIcon variant="light" radius="xl" size={26} color={blank ? 'gray' : 'chartAmber'} style={{ flex: 'none' }}>
              {blank ? <IconFile size={14} /> : <IconTemplate size={14} />}
            </ThemeIcon>
            <Text size="sm" fw={600}>{title}</Text>
          </Group>
          <Text size="xs" style={{ color: TOKENS.textSecondary }}>{description}</Text>
          {steps.length > 0 && (
            <Text size="xs" c="dimmed">{steps.join(', then ')}</Text>
          )}
        </Stack>
      </Card>
    </UnstyledButton>
  );
}
